export interface ScenarioItem {
  id: string;
  scenario: string;
  scenarioTag: string;
  icon: string;
  xmValue: string;
  xmDetail: string;
  axiValue: string;
  axiDetail: string;
  winner: "xm" | "axi" | "tie";
  reason: string;
}

export const scenarioData: ScenarioItem[] = [
  {
    id: "scalping",
    scenario: "High-Frequency Scalping / Day Trading",
    scenarioTag: "Scalping",
    icon: "ri-flashlight-line",
    xmValue: "EUR/USD spread ~1.0 (Standard)",
    xmDetail: "Zero commission on Standard accounts. Solid for moderate-frequency traders with reliable execution. Ultra Low account offers spreads from 0.6 pips with zero commission — the tightest pure-spread option.",
    axiValue: "EUR/USD spread avg 0.0 (Pro), <30ms execution",
    axiDetail: "True ECN pricing with raw interbank spreads from 0.0 pips. Average execution under 30ms via Equinix NY4/LD5 data centers. Pro account at $7/round-turn commission. TradingView direct chart trading built into Axi App.",
    winner: "axi",
    reason: "Axi Wins: Raw spreads from 0.0 pips + sub-30ms execution + TradingView integration make Axi a scalper's dream. At 10+ lots/day, the speed difference alone can be worth 1-2 pips per trade versus slower brokers."
  },
  {
    id: "beginner",
    scenario: "Small Capital / Getting Started",
    scenarioTag: "Beginners",
    icon: "ri-seedling-line",
    xmValue: "Welcome bonus + deposit match, $5 min",
    xmDetail: "$30 no-deposit bonus + 100% welcome bonus up to $500. Start trading with as little as $5. Massive educational library with live webinars. Zero-commission Micro and Standard accounts.",
    axiValue: "No bonuses, $5 minimum deposit",
    axiDetail: "Standard account with commission-free trading, avg 0.7 pips (EUR/USD). $5 minimum deposit means low barrier to entry — but no welcome bonuses or free trial money. Strong educational content through Axi Academy. Axi Select requires $500 to join the funded trader pathway.",
    winner: "xm",
    reason: "XM Wins: $30 no-deposit bonus lets beginners trade real markets risk-free. Axi's $0 minimum is great, but free money to trade with beats free account opening every time. XM's Micro lots (1,000 units) also let beginners practice position sizing with pocket change."
  },
  {
    id: "swing",
    scenario: "Swing Trading / Position Holding",
    scenarioTag: "Swing Trading",
    icon: "ri-moon-line",
    xmValue: "ASIC/CySEC regulated, standard swaps",
    xmDetail: "Multi-regulated with solid investor protection. Standard overnight swap fees at industry average. 1,000+ instruments including stocks, forex, and commodities for diversified swing portfolios.",
    axiValue: "FCA-regulated, £120K FSCS protection",
    axiDetail: "FCA regulation with FSCS investment protection up to £85,000 for UK clients. Competitive swap rates. 1500+ instruments across forex, indices, commodities, shares, and crypto. ASIC, DFSA, and FMA also licensed.",
    winner: "tie",
    reason: "Too Close to Call: XM offers more instruments (1,000+ vs 1500+), but Axi's FCA license with £120K FSCS coverage gives UK/EU swing traders unmatched fund security. Choose XM for diversification, Axi for top-tier regulatory protection."
  },
  {
    id: "news",
    scenario: "News Trading / High-Impact Events",
    scenarioTag: "News Events",
    icon: "ri-newspaper-line",
    xmValue: "99.35% execution rate, near-zero requotes",
    xmDetail: "99.35% of orders executed in under 1 second. Explicit no-requote policy. Rock-solid execution during NFP, FOMC, and black-swan events with strict slippage control.",
    axiValue: "Sub-30ms execution, ECN transparency",
    axiDetail: "Industry-leading execution speed under 30ms via Equinix data centers. ECN model means real market fills with both positive and negative slippage possible during extreme events. 14+ liquidity providers for deep market depth.",
    winner: "xm",
    reason: "XM Wins: Fear no NFP. XM's explicit no-requote guarantee and 99.35% execution rate during news events is top-tier. Axi's ECN fills are faster but raw pricing gaps during extreme volatility. XM's controlled approach is safer for news traders."
  }
];

export interface DownsideItem {
  text: string;
  icon: string;
}

export interface BrokerHonestTake {
  pros: DownsideItem[];
  cons: DownsideItem[];
}

export const xmHonestTake: BrokerHonestTake = {
  pros: [
    {
      text: "$30 no-deposit bonus + 100% welcome bonus up to $500. Trade with the broker's money before risking your own.",
      icon: "ri-check-line"
    },
    {
      text: "99.35% order execution rate in under 1 second. Virtually zero requotes even during major news events.",
      icon: "ri-check-line"
    },
    {
      text: "Ultra Low account with zero commissions and spreads from 0.6 pips. Pure spread model, no hidden fees.",
      icon: "ri-check-line"
    },
    {
      text: "ASIC, CySEC, FSC multi-regulated. Segregated client funds and negative balance protection for maximum security.",
      icon: "ri-check-line"
    },
    {
      text: "Free VPS for EA traders + XMP loyalty points on every trade. Trade more, earn back more.",
      icon: "ri-check-line"
    }
  ],
  cons: [
    {
      text: "Standard account spreads are merely average at 1.0 pip. Without bonuses, XM isn't the cheapest on raw spread cost alone.",
      icon: "ri-emotion-sad-line"
    },
    {
      text: "Limited cryptocurrency pairs and higher stock CFD spreads compared to dedicated equity brokers.",
      icon: "ri-stock-line"
    },
    {
      text: "Bonus terms come with trading volume requirements. The free money isn't withdrawable until you trade enough lots.",
      icon: "ri-file-list-3-line"
    }
  ]
};

export const axiHonestTake: BrokerHonestTake = {
  pros: [
    {
      text: "Average execution speed under 30 milliseconds via Equinix NY4/LD5 data centers. Among the absolute fastest in the retail forex industry — 30x faster than the 1-second industry standard.",
      icon: "ri-check-line"
    },
    {
      text: "Axi Select funded trader programme — trade your way up to $1M in funding with up to 90% profit split at Pro M level. No evaluation fees, no monthly charges. Minimum $500 own capital to join.",
      icon: "ri-check-line"
    },
    {
      text: "FCA regulation with FSCS protection up to £120,000 — significantly higher than the standard £85,000. Five regulatory licenses: FCA, ASIC, DFSA, FMA, and FSA.",
      icon: "ri-check-line"
    },
    {
      text: "$5 minimum deposit on Standard account — low barrier to open and start trading. Pro account requires $500 for raw ECN pricing access.",
      icon: "ri-check-line"
    },
    {
      text: "Proprietary Axi Trading App with built-in TradingView charts. Execute directly from TradingView without third-party bridges or workarounds.",
      icon: "ri-check-line"
    }
  ],
  cons: [
    {
      text: "No deposit bonuses or promotional offers whatsoever. Zero welcome bonus, zero deposit match, zero loyalty points. If you're hunting for freebies, Axi offers nothing.",
      icon: "ri-alert-line"
    },
    {
      text: "$10 monthly inactivity fee after 12 months of dormancy. Not aggressive, but seasonal traders who go quiet for extended periods should stay aware.",
      icon: "ri-time-line"
    },
    {
      text: "No physical stock trading. Axi only offers CFDs — you cannot buy underlying shares or receive dividend entitlements like XM's Shares account provides.",
      icon: "ri-stock-line"
    }
  ]
};

export interface AccountMatchup {
  id: string;
  title: string;
  subtitle: string;
  xmAccount: {
    name: string;
    spread: string;
    commission: string;
    minDeposit: string;
    leverage: string;
    highlight: string;
    pros: string[];
  };
  axiAccount: {
    name: string;
    spread: string;
    commission: string;
    minDeposit: string;
    leverage: string;
    highlight: string;
    pros: string[];
  };
  analysis: string;
}

export const accountMatchups: AccountMatchup[] = [
  {
    id: "entry",
    title: "The Entry-Level Face-Off: Where Beginners Land",
    subtitle: "XM Micro Account vs Axi Standard Account — the first account most new traders sign up for",
    xmAccount: {
      name: "XM Micro",
      spread: "EUR/USD from 1.0 pip",
      commission: "Zero commission",
      minDeposit: "$5",
      leverage: "1:1000",
      highlight: "1 micro lot = 1,000 units",
      pros: ["$5 minimum — lowest in the industry", "1 micro lot trading for risk management practice", "Zero commission — all cost is the spread", "Eligible for $30 no-deposit welcome bonus"]
    },
    axiAccount: {
      name: "Axi Standard",
      spread: "EUR/USD avg 0.7 pips",
      commission: "Zero commission",
      minDeposit: "$5",
      leverage: "Up to 1000:1",
      highlight: "Tighter spread + $5 entry",
      pros: ["$5 minimum deposit — low barrier to open", "Avg 0.7 pips — tighter than XM Standard's 1.0 pip", "Zero commission — simple spread-only pricing", "Full MT4/MT5 access with 1500+ instruments", "Sub-30ms ECN execution under the hood"]
    },
    analysis: "Both accounts let you start with just $5. XM Micro uses 1,000-unit micro lots with the $30 no-deposit bonus. Axi Standard offers tighter 0.7 pip spreads but uses standard lot sizing. The verdict: if you want a free trial with the broker's money, XM Micro wins with its $30 bonus. If you want the tightest spreads from day one at the same $5 entry, Axi Standard offers genuine value."
  },
  {
    id: "standard",
    title: "The Mainstream Battle: Everyday Trading Made Simple",
    subtitle: "XM Standard Account vs Axi Pro Account — cost comparison for daily traders",
    xmAccount: {
      name: "XM Standard",
      spread: "EUR/USD from 1.0 pip",
      commission: "Zero commission",
      minDeposit: "$5",
      leverage: "1:1000",
      highlight: "Most popular — classic spread-only",
      pros: ["Zero commission — pay only the spread", "1 standard lot = 100,000 units, full-size trading", "Eligible for all XM bonuses including $30 no-deposit", "Free daily market analysis and webinars", "XMP loyalty points earned on every trade"]
    },
    axiAccount: {
      name: "Axi Pro",
      spread: "EUR/USD avg 0.0 pips (raw ECN)",
      commission: "$2.25/side ($4.50/round trip)",
      minDeposit: "$500",
      leverage: "Up to 1000:1",
      highlight: "True ECN — raw interbank pricing",
      pros: ["Raw spreads averaging 0.0 pips — true interbank pricing", "$4.50/round-trip commission — competitive ECN rate", "$500 minimum deposit for raw ECN access", "Sub-30ms execution via Equinix NY4/LD5"]
    },
    analysis: "Here's the hard math: XM Standard at 1.0 pip = ~$10/lot all-in (zero commission). Axi Pro at avg 0.0 pips + $4.50/lot commission = ~$4.50/lot all-in. Axi Pro is actually cheaper per lot by about $5.50, but requires a $500 minimum deposit while XM Standard opens with just $5. XM offers bonuses and loyalty points; Axi offers tighter spreads and raw ECN execution. For pure trading cost at any meaningful volume, Axi Pro wins. For smaller budgets under $500, XM Standard at $5 minimum is the accessible choice."
  },
  {
    id: "professional",
    title: "The Professional Tier: Big Capital, Bigger Decisions",
    subtitle: "XM Ultra Low vs Axi Elite — when you're trading serious volume",
    xmAccount: {
      name: "XM Ultra Low",
      spread: "EUR/USD from 0.6 pips",
      commission: "Zero commission",
      minDeposit: "$50",
      leverage: "1:1000",
      highlight: "XM's tightest — zero commission",
      pros: ["From 0.6 pips — XM's tightest pure-spread account", "Zero commission — no per-trade fees", "Available in Micro + Standard lot sizes", "$50 minimum — accessible for tight-spread trading"]
    },
    axiAccount: {
      name: "Axi Elite",
      spread: "EUR/USD from 0.0 pips, avg 0.0",
      commission: "$1.75/side ($3.50/round trip)",
      minDeposit: "$25,000",
      leverage: "Up to 1000:1",
      highlight: "Half-price commission — industry elite",
      pros: ["Reduced commission: $3.50/round-trip vs $4.50 Pro rate", "Raw interbank spreads averaging 0.0 pips — tightest possible", "Dedicated account manager + priority support", "Gateway to Axi Select: up to $1M funding, 90% profit split"]
    },
    analysis: "At 50+ lots/month, the math shifts dramatically: XM Ultra Low at 0.6 pips = $300/month all-in. Axi Elite at avg 0.0 pips + $3.50/lot commission = $175/month all-in. Axi Elite saves $125/month — and that's at just 50 lots. At 200 lots/month, the saving exceeds $500. Plus Axi Elite includes dedicated account manager, free VPS, FIX API, and unlocks the Axi Select pathway with up to $1M funding and 90% profit split. For professional traders with $25K+ capital, Axi Elite is genuinely compelling."
  }
];

export interface TechComparison {
  category: string;
  icon: string;
  xm: { label: string; detail: string; score: number };
  axi: { label: string; detail: string; score: number };
}

export const techData: TechComparison[] = [
  {
    category: "Trading Platform Options",
    icon: "ri-computer-line",
    xm: { label: "MT4/MT5 + TradingView Analysis", detail: "Full MT4 and MT5 support with MQL community signals. TradingView integration for chart-based analysis only — not direct execution. No proprietary platform.", score: 7.0 },
    axi: { label: "MT4/MT5 + Axi App (TradingView Built-in)", detail: "Full MT4/MT5 support plus proprietary Axi Trading App with TradingView charts built directly in. Execute trades from TradingView without third-party bridges. Clean, modern interface for mobile-first traders.", score: 8.5 }
  },
  {
    category: "Copy Trading Ecosystem",
    icon: "ri-group-line",
    xm: { label: "MQL5 Community Reliant", detail: "Copy trading primarily through MQL5 community signals. No dedicated proprietary copy trading platform. Functional but not specialized.", score: 5.5 },
    axi: { label: "ZuluTrade + Myfxbook + Axi Copy", detail: "Three distinct copy trading channels: ZuluTrade (largest third-party leaderboard), Myfxbook AutoTrade (verified track records), and Axi's proprietary copy trading platform. Strong ecosystem for strategy followers.", score: 8.0 }
  },
  {
    category: "Mobile App Experience",
    icon: "ri-smartphone-line",
    xm: { label: "Standard MT4/MT5 Mobile", detail: "Relies on standard MetaTrader mobile apps. Solid for order management and basic charting but no proprietary mobile experience.", score: 7.0 },
    axi: { label: "Axi Trading App + MT Apps", detail: "Proprietary Axi Trading App with TradingView charts, simplified account management, and one-tap trading. Alongside standard MT4/MT5 mobile. Modern UI that beginners actually enjoy using.", score: 8.5 }
  },
  {
    category: "API & Automation Support",
    icon: "ri-code-line",
    xm: { label: "MT4/MT5 Standard API", detail: "Full MQL4/MQL5 support with rich community resources. Free VPS for EA traders. No standalone REST API.", score: 7.5 },
    axi: { label: "MQL + VPS + Equinix Hosting", detail: "Full MQL support for MT platforms. Premium VPS hosting near Equinix NY4/LD5 for lowest latency. 14+ liquidity providers with fiber cross-connects. Ideal for latency-sensitive algorithmic strategies.", score: 8.5 }
  }
];

export interface PerkItem {
  icon: string;
  title: string;
  description: string;
}

export const xmPerks: PerkItem[] = [
  {
    icon: "ri-gift-2-line",
    title: "$30 No-Deposit Bonus",
    description: "Claim it right after verification. Trade real markets without depositing a cent first. The ultimate risk-free test drive."
  },
  {
    icon: "ri-vip-diamond-line",
    title: "Loyalty Points Program (XMP)",
    description: "Earn points on every trade. Redeem for cash or bonus credit. The more you trade, the more you earn back — effectively lowering your spread costs."
  },
  {
    icon: "ri-cloud-line",
    title: "Free VPS Service",
    description: "Qualify with minimum deposit and volume requirements. Perfect for traders running EAs 24/7 without keeping their PC on."
  },
  {
    icon: "ri-percent-line",
    title: "100% Welcome Deposit Bonus",
    description: "Double your first deposit up to $500. Deposit $500, trade with $1,000. The most aggressive bonus in the industry."
  }
];

export const axiPerks: PerkItem[] = [
  {
    icon: "ri-funds-line",
    title: "Axi Select — Up to $1M Funding",
    description: "Trade your way from Seed to Pro M with up to $1 million in Axi capital and 90% profit split. No evaluation fees, no monthly charges. Start with just $500 of your own money and a solid track record. Six progressive stages reward consistency over speed."
  },
  {
    icon: "ri-speed-up-line",
    title: "Sub-30ms Execution Speed",
    description: "Average order execution under 30 milliseconds via Equinix NY4/LD5 data centers. 14+ liquidity providers with dedicated fiber cross-connects. Among the absolute fastest execution in the retail forex industry — over 30x faster than the typical 1-second benchmark."
  },
  {
    icon: "ri-shield-star-line",
    title: "FCA + £85K FSCS Investment Protection",
    description: "UK FCA regulation with FSCS investment protection up to £85,000 for UK clients. Five regulatory licenses: FCA, ASIC, DFSA, FMA, and FSA."
  },
  {
    icon: "ri-smartphone-line",
    title: "Axi App + TradingView Built-in",
    description: "Proprietary Axi Trading App with TradingView charts integrated directly. Execute trades from TradingView without MT4/MT5. Clean interface, one-tap order management, and full account control. Modern mobile-first experience that makes traditional MT apps feel dated."
  }
];

export interface BlackSwanPoint {
  category: string;
  xm: string;
  axi: string;
  xmAdvantage: boolean;
}

export const blackSwanData: BlackSwanPoint[] = [
  {
    category: "Order Execution (News Events)",
    xm: "99.35% of orders executed in under 1 second. Zero requotes policy. Rock-solid during NFP, FOMC, and black-swan market events.",
    axi: "Sub-30ms average execution with true ECN fills. During extreme news events, spreads widen naturally as liquidity thins — real market behavior, not broker intervention. Both positive and negative slippage possible.",
    xmAdvantage: true
  },
  {
    category: "Slippage Management",
    xm: "Explicitly guarantees no requotes. Strict slippage control. Most orders filled at requested price or better during normal conditions.",
    axi: "ECN model reflects genuine market depth. During fast markets, fills may slip but also offer positive slippage opportunities. Transparent but less controlled than XM's managed approach.",
    xmAdvantage: true
  },
  {
    category: "Fund Security & Compensation",
    xm: "Segregated client funds and negative balance protection. Multi-jurisdiction regulatory framework across ASIC, CySEC, and FSC.",
    axi: "FCA regulation since 2007 with FSCS investment protection up to £85,000 for UK clients. FCA, ASIC, DFSA, FMA, and FSA licensed. Segregated accounts at AA-rated banks. Nearly 20-year operational history.",
    xmAdvantage: false
  },
  {
    category: "Platform Stability During Crises",
    xm: "MT4/MT5 infrastructure is battle-tested. During black-swan events, server stability is reliable but execution speed can slow under extreme load.",
    axi: "Equinix NY4/LD5 hosting with dedicated fiber cross-connects to 14+ liquidity providers. Multiple redundant servers across data centers. Sub-30ms execution maintained even during peak volatility events.",
    xmAdvantage: false
  }
];

export interface QuizQuestion {
  id: string;
  question: string;
  options: { label: string; value: string }[];
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: "capital",
    question: "What's your starting capital?",
    options: [
      { label: "Under $100 — Just testing the waters", value: "small" },
      { label: "$100 - $5,000 — Got a decent budget", value: "medium" },
      { label: "$5,000+ — Professional level", value: "large" }
    ]
  },
  {
    id: "style",
    question: "What's your trading style?",
    options: [
      { label: "Scalping / High-frequency — Dozens to hundreds of trades daily", value: "scalping" },
      { label: "Day trading — A few trades per day", value: "daytrade" },
      { label: "Swing trading / Position holding — Days to weeks", value: "swing" }
    ]
  },
  {
    id: "priority",
    question: "What matters most to you?",
    options: [
      { label: "Lightning-fast execution and the lowest possible spreads — every millisecond counts", value: "speed" },
      { label: "Bonuses, promotions, and free money to boost my account", value: "bonus" },
      { label: "A funded trader pathway — I want to trade institutional capital", value: "funding" }
    ]
  }
];

export function computeQuizResult(answers: Record<string, string>): { broker: "xm" | "axi"; percentage: number; reason: string } {
  const { capital, style, priority } = answers;
  let xmScore = 0;
  let axiScore = 0;

  // Trading style scoring
  if (style === "scalping") {
    axiScore += 35;
    xmScore += 10;
  } else if (style === "daytrade") {
    axiScore += 20;
    xmScore += 20;
  } else {
    xmScore += 20;
    axiScore += 10;
  }

  // Capital scoring
  if (capital === "small") {
    xmScore += 30;
    axiScore += 10;
  } else if (capital === "large") {
    axiScore += 25;
    xmScore += 10;
  } else {
    axiScore += 15;
    xmScore += 15;
  }

  // Priority scoring
  if (priority === "speed") {
    axiScore += 30;
    xmScore += 5;
  } else if (priority === "bonus") {
    xmScore += 35;
    axiScore += 0;
  } else {
    axiScore += 30;
    xmScore += 5;
  }

  const total = xmScore + axiScore;
  if (xmScore >= axiScore) {
    return {
      broker: "xm",
      percentage: Math.round((xmScore / total) * 100),
      reason: xmScore > axiScore * 1.2
        ? "Your trading style is a strong match for XM: low entry barrier, generous bonuses, and rock-solid execution during news events. Perfect for beginners, bonus hunters, and news traders. Start with the $30 no-deposit bonus to trade risk-free."
        : "You lean toward XM, but Axi is a solid alternative — especially if execution speed matters. XM's $5 minimum deposit and bonus program give you an edge — start with the $30 no-deposit bonus to test the waters."
    };
  }
  return {
    broker: "axi",
    percentage: Math.round((axiScore / total) * 100),
    reason: axiScore > xmScore * 1.3
      ? "Your trading style is built for Axi: sub-30ms execution, raw ECN spreads from 0.0 pips, TradingView integration, and the Axi Select programme with up to $1M funding and 90% profit split. Scalpers and aspiring funded traders will find their optimal setup here."
      : "You lean toward Axi, but XM's bonuses and lower minimum deposit are worth considering. Try Axi's Pro account for raw 0.0 pip spreads with zero minimum deposit — then explore Axi Select once you've built a track record."
  };
}

export interface AccountTypeItem {
  name: string;
  highlight: string;
  spread: string;
  commission: string;
  minDeposit: string;
  leverage: string;
  note: string;
  features: string[];
  bestFor: string;
}

export const xmAccountTypes: AccountTypeItem[] = [
  {
    name: "Micro Account",
    highlight: "Ultra-low entry barrier",
    spread: "From 1.0 pip (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$5",
    leverage: "Up to 1:1000",
    note: "Contracts are 1,000 base currency units (1 micro lot). Perfect for absolute beginners who want to learn with minimal risk. Same execution quality as larger accounts — no discrimination based on account size.",
    features: [
      "Trade with as little as $5 — the industry's lowest barrier",
      "1 micro lot = 1,000 units, ideal for position sizing practice",
      "Full MT4/MT5 access with all 1,000+ instruments",
      "Negative balance protection included standard",
      "Same spreads and execution as Standard account"
    ],
    bestFor: "Complete beginners testing the waters with pocket change"
  },
  {
    name: "Standard Account",
    highlight: "Most popular — classic spread-only",
    spread: "From 1.0 pip (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$5",
    leverage: "Up to 1:1000",
    note: "XM's flagship account. 1 standard lot = 100,000 units. No commissions, just the raw spread. This is the account most XM traders use — reliable, simple, and backed by 99.35% execution rates.",
    features: [
      "Zero commission — pay only the spread, nothing more",
      "1 standard lot = 100,000 units, full-size trading",
      "Eligible for all XM bonuses including $30 no-deposit",
      "Free daily market analysis and webinars",
      "XMP loyalty points earned on every trade"
    ],
    bestFor: "The everyday trader who wants bonus perks + no commission complexity"
  },
  {
    name: "Ultra Low Account",
    highlight: "Tightest XM spreads — zero commission",
    spread: "From 0.6 pips (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$50",
    leverage: "Up to 1:1000",
    note: "XM's tightest spread offering. Spreads start at 0.6 pips on EUR/USD — nearly half the Standard account — with zero commissions. Available in both Micro and Standard lot sizes. For moderate to active traders who want tight pricing without per-trade commission calculations.",
    features: [
      "Spreads from 0.6 pips — XM's tightest pure-spread offering",
      "Zero commission — all cost is in the spread",
      "Available in Micro + Standard lot sizes",
      "Same 1,000+ instruments and platform access",
      "Ideal sweet spot between Micro and high-volume accounts"
    ],
    bestFor: "Cost-aware traders wanting tight spreads without commission math"
  },
  {
    name: "Shares Account",
    highlight: "1,200+ global stocks — exchange pricing",
    spread: "Exchange-level real stock spreads",
    commission: "Per-share commission (varies by exchange)",
    minDeposit: "$10,000",
    leverage: "No leverage (physical shares)",
    note: "XM's dedicated stock trading account. Direct access to 1,200+ individual shares across US, UK, and EU exchanges — not CFDs, actual share trading with ownership rights. Leverage is not available. Designed for investors who want exchange-grade pricing and dividend entitlements.",
    features: [
      "Trade 1,200+ individual shares across global exchanges",
      "Exchange-grade pricing with transparent per-share commissions",
      "Dividend rights included on long positions",
      "Access to US, UK, EU stock exchanges",
      "Physical share ownership — different from CFDs"
    ],
    bestFor: "Stock investors wanting real exchange access, not CFD exposure"
  }
];

export const axiAccountTypes: AccountTypeItem[] = [
  {
    name: "Standard Account",
    highlight: "Commission-free, tighter spreads",
    spread: "Avg 0.7 pips (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$5",
    leverage: "Up to 1000:1",
    note: "Axi's entry-level account with commission-free trading and average spreads of 0.7 pips on EUR/USD — tighter than many brokers' standard accounts. Zero commission and $5 minimum deposit. Backed by the same ECN execution infrastructure as the Pro account. Ideal for traders who want tight pricing without per-trade commission calculations.",
    features: [
      "Avg 0.7 pips — tighter than many standard accounts",
      "Zero commission — pay only the spread, nothing more",
      "$5 minimum deposit — low barrier to open and start",
      "Full MT4/MT5 access with 1500+ instruments",
      "Sub-30ms ECN execution backbone under the hood"
    ],
    bestFor: "Traders wanting tight spreads without commission — at low entry cost"
  },
  {
    name: "Pro Account",
    highlight: "True ECN — raw interbank pricing",
    spread: "Avg 0.0 pips (EUR/USD)",
    commission: "$2.25 per side ($4.50 round trip)",
    minDeposit: "$500",
    leverage: "Up to 1000:1",
    note: "Axi's flagship ECN account. True raw interbank spreads averaging 0.0 pips with $4.50/round-trip commission. $500 minimum deposit for raw interbank pricing on one of the fastest execution engines in retail forex. Powered by 14+ liquidity providers via Equinix NY4/LD5 with sub-30ms average execution. Ideal for active traders who want transparent, institutional-grade pricing.",
    features: [
      "Raw interbank spreads averaging 0.0 pips — true ECN pricing",
      "$4.50/round-trip commission — competitive ECN rate",
      "$500 minimum deposit for raw interbank ECN pricing",
      "Sub-30ms execution via Equinix data centers",
      "14+ liquidity providers for deep market depth"
    ],
    bestFor: "Active traders wanting raw ECN pricing with professional execution"
  },
  {
    name: "Elite Account",
    highlight: "Half-price commission — professional tier",
    spread: "Avg 0.0 pips (EUR/USD)",
    commission: "$1.75 per side ($3.50 round trip)",
    minDeposit: "$25,000",
    leverage: "Up to 1000:1",
    note: "Axi's premium tier for professional traders. Same raw interbank spreads averaging 0.0 pips but at a reduced commission rate — just $3.50/round-trip. Dedicated account manager, priority withdrawals, and exclusive market analysis. At 50+ lots/month, the commission savings are substantial versus the Pro account.",
    features: [
      "Reduced commission: $3.50/round-trip vs $4.50 Pro rate",
      "Raw interbank spreads averaging 0.0 pips — tightest possible",
      "Dedicated personal account manager + priority support",
      "Exclusive market analysis and analytics tools",
      "Free VPS + FIX API and institutional-grade infrastructure"
    ],
    bestFor: "Professional traders with $25K+ doing 50+ lots/month"
  }
];